import './AttachedFileButton.scss';

import { useCallback } from 'react';

import { message } from 'antd';
import { BiUpload } from 'react-icons/bi';

import AttachedFileItem from '../../../../components/AttachedFileItem/AttachedFileItem';
import {
  FileType,
  uploadFiles,
  uploadFilesHandler,
} from '../../../../shared/file';
import { AttachedFileDisplay } from '../CreateServiceRequestForm/CreateServiceRequestForm';

const MAX_FILES = 4;
const MAX_FILE_SIZE_MB = 5;

const ALLOWED_FILE_TYPES = [
  'pdf',
  'jpg',
  'jpeg',
  'png',
  'docx',
  'pptx',
  'xlsx',
  'txt',
] as const;

const AttachedFileButton = ({
  attachedFiles,
  setAttachedFiles,
}: {
  attachedFiles: AttachedFileDisplay[];
  setAttachedFiles: (files: AttachedFileDisplay[]) => void;
}) => {
  const handleFileUpload = useCallback(() => {
    if (attachedFiles.length >= MAX_FILES) {
      message.warning(`You can upload up to ${MAX_FILES} files.`);
      return;
    }

    uploadFilesHandler({
      type: FileType.DOC,
      mode: 'multiple',
      accept: [...ALLOWED_FILE_TYPES] as unknown as Parameters<
        typeof uploadFilesHandler
      >[0]['accept'],
      onUpload: (files, type) => {
        const remaining = MAX_FILES - attachedFiles.length;
        const toAdd = files.slice(0, remaining);

        uploadFiles({
          files: toAdd,
          type,
          accept: [...ALLOWED_FILE_TYPES] as unknown as Parameters<
            typeof uploadFiles
          >[0]['accept'],
          maxSizeUnit: 'MB',
          maxSize: MAX_FILE_SIZE_MB,
          onError: (msg) => message.error(msg),
          uploadCallback: (processedFiles) => {
            const newEntries: AttachedFileDisplay[] = processedFiles.map(
              (file, index) => {
                const ext = file.name.split('.').pop()?.toUpperCase() ?? 'FILE';
                return {
                  id: `${Date.now()}-${index}-${file.name}`,
                  file,
                  name: file.name,
                  size: file.size,
                  typeLabel: ext,
                };
              },
            );
            const combined = [...attachedFiles, ...newEntries].slice(
              0,
              MAX_FILES,
            );
            setAttachedFiles(combined);
            if (files.length > remaining) {
              message.warning(
                `Only the first ${remaining} file(s) were added. Max ${MAX_FILES} files.`,
              );
            }
          },
        });
      },
    });
  }, [attachedFiles, setAttachedFiles]);

  const removeFile = useCallback(
    (id: string) => {
      const newFiles = attachedFiles.filter((f) => f.id !== id);
      setAttachedFiles(newFiles);
    },
    [setAttachedFiles, attachedFiles],
  );
  return (
    <div className="attached-file-button">
      <label className="attached-file-button__label">
        Attach Files(Optional)
      </label>
      <p className="attached-file-button__hint">
        You can upload up to {MAX_FILES} files (Max {MAX_FILE_SIZE_MB} MB each)
        in PDF, JPG, PNG, DOCX, PPTX, XLSX, or TXT format.
      </p>
      <div className="attached-file-button__files">
        {attachedFiles.map((item) => (
          <AttachedFileItem
            key={item.id}
            fileName={item.name}
            fileSize={item.size}
            fileExtension={item.typeLabel.toLowerCase()}
            onRemove={() => removeFile(item.id)}
          />
        ))}
      </div>
      {attachedFiles.length < MAX_FILES && (
        <button
          type="button"
          className="attached-file-button__upload-btn"
          onClick={handleFileUpload}>
          <BiUpload size={20} />
          Click to upload
        </button>
      )}
    </div>
  );
};

export default AttachedFileButton;
